/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  FlightRule,
  MissionConfiguration,
  MissionProfile,
  OperationType,
  RequestInitiationType,
  RequestStatus,
  TransportOfferAlgorithm,
  VehicleType,
  WebOrderCallType,
} from 'src/graphql/generated/graphql.generated';
import {
  SafetyQuestionsRecord,
  SafetyQuestionsProps,
} from 'src/store/generated/safety-questions-record.generated';
import {
  RequiredCapabilityRecord,
  RequiredCapabilityProps,
} from 'src/store/generated/required-capability-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RequestProps {
  id: string;
  status: RequestStatus;
  cancellationReasonId: string | null;
  cancellationSecondaryReasonId: string | null;
  cancellationReasonNotes: string | null;
  requestedVehicleType: VehicleType | null;
  requestedOperationType: OperationType;
  primaryUnitServiceAreaId: string | null;
  isStandbyRequest: boolean;
  commitUnitToStandby: boolean | null;
  standbyActivationRequested: boolean;
  initiationType: RequestInitiationType | null;
  safetyQuestions: SafetyQuestionsProps;
  assignmentDelayedTime: Date | null;
  assignmentDelayedReasonId: string | null;
  regionId: string | null;
  includeCompetitors: boolean | null;
  transportOfferAlgorithmId: string | null;
  transportOfferAlgorithm: TransportOfferAlgorithm | null;
  minimumFlightRule: FlightRule | null;
  requiredCapabilities: List<RequiredCapabilityProps>;
  missionConfiguration: MissionConfiguration;
  missionProfile: MissionProfile;
  transportTrackingLink: string | null;
  latestWeatherDeclineUnitId: string | null;
  latestWeatherDeclineTime: Date | null;
  latestWeatherDeclineReasonId: string | null;
  latestWeatherDeclineSecondaryReasonId: string | null;
  webOrderCallType: WebOrderCallType | null;
  etloInMinutes: number | null;
  atloInMinutes: number | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RequestRecord extends Record<RequestProps>({
  id: null as any,
  status: null as any,
  cancellationReasonId: null as any,
  cancellationSecondaryReasonId: null as any,
  cancellationReasonNotes: null as any,
  requestedVehicleType: null as any,
  requestedOperationType: null as any,
  primaryUnitServiceAreaId: null as any,
  isStandbyRequest: null as any,
  commitUnitToStandby: null as any,
  standbyActivationRequested: null as any,
  initiationType: null as any,
  safetyQuestions: null as any,
  assignmentDelayedTime: null as any,
  assignmentDelayedReasonId: null as any,
  regionId: null as any,
  includeCompetitors: null as any,
  transportOfferAlgorithmId: null as any,
  transportOfferAlgorithm: null as any,
  minimumFlightRule: null as any,
  requiredCapabilities: null as any,
  missionConfiguration: null as any,
  missionProfile: null as any,
  transportTrackingLink: null as any,
  latestWeatherDeclineUnitId: null as any,
  latestWeatherDeclineTime: null as any,
  latestWeatherDeclineReasonId: null as any,
  latestWeatherDeclineSecondaryReasonId: null as any,
  webOrderCallType: null as any,
  etloInMinutes: null as any,
  atloInMinutes: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: RequestStatus;
    cancellationReasonId: string | null;
    cancellationSecondaryReasonId: string | null;
    cancellationReasonNotes: string | null;
    requestedVehicleType: VehicleType | null;
    requestedOperationType: OperationType;
    primaryUnitServiceAreaId: string | null;
    isStandbyRequest: boolean;
    commitUnitToStandby: boolean | null;
    standbyActivationRequested: boolean;
    initiationType: RequestInitiationType | null;
    safetyQuestions: any;
    assignmentDelayedTime: string | number | Date | null;
    assignmentDelayedReasonId: string | null;
    regionId: string | null;
    includeCompetitors: boolean | null;
    transportOfferAlgorithmId: string | null;
    transportOfferAlgorithm: TransportOfferAlgorithm | null;
    minimumFlightRule: FlightRule | null;
    requiredCapabilities: any;
    missionConfiguration: MissionConfiguration;
    missionProfile: MissionProfile;
    transportTrackingLink: string | null;
    latestWeatherDeclineUnitId: string | null;
    latestWeatherDeclineTime: string | number | Date | null;
    latestWeatherDeclineReasonId: string | null;
    latestWeatherDeclineSecondaryReasonId: string | null;
    webOrderCallType: WebOrderCallType | null;
    etloInMinutes: number | null;
    atloInMinutes: number | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RequestRecord {
    if (json == null) {
      throw new Error('RequestRecord.fromJS: parameter cannot be null');
    }
    return new RequestRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RequestJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', RequestStatus.CallReceived),
        'RequestJson.status',
      ),
      cancellationReasonId: json.cancellationReasonId,
      cancellationSecondaryReasonId: json.cancellationSecondaryReasonId,
      cancellationReasonNotes: json.cancellationReasonNotes,
      requestedVehicleType: json.requestedVehicleType,
      requestedOperationType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestedOperationType', OperationType.SceneCall),
        'RequestJson.requestedOperationType',
      ),
      primaryUnitServiceAreaId: json.primaryUnitServiceAreaId,
      isStandbyRequest: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isStandbyRequest', false),
        'RequestJson.isStandbyRequest',
      ),
      commitUnitToStandby: json.commitUnitToStandby,
      standbyActivationRequested: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'standbyActivationRequested', false),
        'RequestJson.standbyActivationRequested',
      ),
      initiationType: json.initiationType,
      safetyQuestions: new SafetyQuestionsRecord(
        getFromOrThrow(json, (j) => j.safetyQuestions, 'RequestJson.safetyQuestions'),
      ),
      assignmentDelayedTime:
        json.assignmentDelayedTime != null ? new Date(json.assignmentDelayedTime) : null,
      assignmentDelayedReasonId: json.assignmentDelayedReasonId,
      regionId: json.regionId,
      includeCompetitors: json.includeCompetitors,
      transportOfferAlgorithmId: json.transportOfferAlgorithmId,
      transportOfferAlgorithm: json.transportOfferAlgorithm,
      minimumFlightRule: json.minimumFlightRule,
      requiredCapabilities: List<RequiredCapabilityRecord>(
        getFromOrThrow(json, (j) => j.requiredCapabilities, 'RequestJson.requiredCapabilities').map(
          (o: any) => new RequiredCapabilityRecord(o),
        ),
      ),
      missionConfiguration: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'missionConfiguration', MissionConfiguration.Unspecified),
        'RequestJson.missionConfiguration',
      ),
      missionProfile: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'missionProfile', MissionProfile.Unspecified),
        'RequestJson.missionProfile',
      ),
      transportTrackingLink: json.transportTrackingLink,
      latestWeatherDeclineUnitId: json.latestWeatherDeclineUnitId,
      latestWeatherDeclineTime:
        json.latestWeatherDeclineTime != null ? new Date(json.latestWeatherDeclineTime) : null,
      latestWeatherDeclineReasonId: json.latestWeatherDeclineReasonId,
      latestWeatherDeclineSecondaryReasonId: json.latestWeatherDeclineSecondaryReasonId,
      webOrderCallType: json.webOrderCallType,
      etloInMinutes: json.etloInMinutes,
      atloInMinutes: json.atloInMinutes,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'RequestJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: RequestStatus;
    cancellationReasonId: string | null;
    cancellationSecondaryReasonId: string | null;
    cancellationReasonNotes: string | null;
    requestedVehicleType: VehicleType | null;
    requestedOperationType: OperationType;
    primaryUnitServiceAreaId: string | null;
    isStandbyRequest: boolean;
    commitUnitToStandby: boolean | null;
    standbyActivationRequested: boolean;
    initiationType: RequestInitiationType | null;
    safetyQuestions: any;
    assignmentDelayedTime: string | number | Date | null;
    assignmentDelayedReasonId: string | null;
    regionId: string | null;
    includeCompetitors: boolean | null;
    transportOfferAlgorithmId: string | null;
    transportOfferAlgorithm: TransportOfferAlgorithm | null;
    minimumFlightRule: FlightRule | null;
    requiredCapabilities: any;
    missionConfiguration: MissionConfiguration;
    missionProfile: MissionProfile;
    transportTrackingLink: string | null;
    latestWeatherDeclineUnitId: string | null;
    latestWeatherDeclineTime: string | number | Date | null;
    latestWeatherDeclineReasonId: string | null;
    latestWeatherDeclineSecondaryReasonId: string | null;
    webOrderCallType: WebOrderCallType | null;
    etloInMinutes: number | null;
    atloInMinutes: number | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RequestRecord, Error[]] {
    if (json == null) {
      throw new Error('RequestRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RequestRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RequestJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', RequestStatus.CallReceived),
            'RequestJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationReasonId: tryOrDefault(
        () => json.cancellationReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationSecondaryReasonId: tryOrDefault(
        () => json.cancellationSecondaryReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationReasonNotes: tryOrDefault(
        () => json.cancellationReasonNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedVehicleType: tryOrDefault(
        () => json.requestedVehicleType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedOperationType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requestedOperationType', OperationType.SceneCall),
            'RequestJson.requestedOperationType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      primaryUnitServiceAreaId: tryOrDefault(
        () => json.primaryUnitServiceAreaId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isStandbyRequest: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isStandbyRequest', false),
            'RequestJson.isStandbyRequest',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      commitUnitToStandby: tryOrDefault(
        () => json.commitUnitToStandby,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      standbyActivationRequested: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'standbyActivationRequested', false),
            'RequestJson.standbyActivationRequested',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      initiationType: tryOrDefault(
        () => json.initiationType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      safetyQuestions: tryOrDefault(
        () =>
          new SafetyQuestionsRecord(
            getFromOrThrow(json, (j) => j.safetyQuestions, 'RequestJson.safetyQuestions'),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assignmentDelayedTime: tryOrDefault(
        () => (json.assignmentDelayedTime != null ? new Date(json.assignmentDelayedTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assignmentDelayedReasonId: tryOrDefault(
        () => json.assignmentDelayedReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () => json.regionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      includeCompetitors: tryOrDefault(
        () => json.includeCompetitors,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportOfferAlgorithmId: tryOrDefault(
        () => json.transportOfferAlgorithmId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportOfferAlgorithm: tryOrDefault(
        () => json.transportOfferAlgorithm,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      minimumFlightRule: tryOrDefault(
        () => json.minimumFlightRule,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requiredCapabilities: tryOrDefault(
        () =>
          List<RequiredCapabilityRecord>(
            getFromOrThrow(
              json,
              (j) => j.requiredCapabilities,
              'RequestJson.requiredCapabilities',
            ).map((o: any) => new RequiredCapabilityRecord(o)),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      missionConfiguration: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'missionConfiguration', MissionConfiguration.Unspecified),
            'RequestJson.missionConfiguration',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      missionProfile: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'missionProfile', MissionProfile.Unspecified),
            'RequestJson.missionProfile',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportTrackingLink: tryOrDefault(
        () => json.transportTrackingLink,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestWeatherDeclineUnitId: tryOrDefault(
        () => json.latestWeatherDeclineUnitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestWeatherDeclineTime: tryOrDefault(
        () =>
          json.latestWeatherDeclineTime != null ? new Date(json.latestWeatherDeclineTime) : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestWeatherDeclineReasonId: tryOrDefault(
        () => json.latestWeatherDeclineReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestWeatherDeclineSecondaryReasonId: tryOrDefault(
        () => json.latestWeatherDeclineSecondaryReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      webOrderCallType: tryOrDefault(
        () => json.webOrderCallType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      etloInMinutes: tryOrDefault(
        () => json.etloInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      atloInMinutes: tryOrDefault(
        () => json.atloInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'RequestJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RequestProps) {
    super(props);
    if (props == null) {
      throw new Error('RequestRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RequestViewModelBase implements RequestProps {
  protected readonly _props: RequestProps;
  private _json?: RequestProps;

  get id(): string {
    return this._props.id;
  }
  get status(): RequestStatus {
    return this._props.status;
  }
  get cancellationReasonId(): string | null {
    return this._props.cancellationReasonId;
  }
  get cancellationSecondaryReasonId(): string | null {
    return this._props.cancellationSecondaryReasonId;
  }
  get cancellationReasonNotes(): string | null {
    return this._props.cancellationReasonNotes;
  }
  get requestedVehicleType(): VehicleType | null {
    return this._props.requestedVehicleType;
  }
  get requestedOperationType(): OperationType {
    return this._props.requestedOperationType;
  }
  get primaryUnitServiceAreaId(): string | null {
    return this._props.primaryUnitServiceAreaId;
  }
  get isStandbyRequest(): boolean {
    return this._props.isStandbyRequest;
  }
  get commitUnitToStandby(): boolean | null {
    return this._props.commitUnitToStandby;
  }
  get standbyActivationRequested(): boolean {
    return this._props.standbyActivationRequested;
  }
  get initiationType(): RequestInitiationType | null {
    return this._props.initiationType;
  }
  get safetyQuestions(): SafetyQuestionsProps {
    return this._props.safetyQuestions;
  }
  get assignmentDelayedTime(): Date | null {
    return this._props.assignmentDelayedTime;
  }
  get assignmentDelayedReasonId(): string | null {
    return this._props.assignmentDelayedReasonId;
  }
  get regionId(): string | null {
    return this._props.regionId;
  }
  get includeCompetitors(): boolean | null {
    return this._props.includeCompetitors;
  }
  get transportOfferAlgorithmId(): string | null {
    return this._props.transportOfferAlgorithmId;
  }
  get transportOfferAlgorithm(): TransportOfferAlgorithm | null {
    return this._props.transportOfferAlgorithm;
  }
  get minimumFlightRule(): FlightRule | null {
    return this._props.minimumFlightRule;
  }
  get requiredCapabilities(): List<RequiredCapabilityProps> {
    return this._props.requiredCapabilities;
  }
  get missionConfiguration(): MissionConfiguration {
    return this._props.missionConfiguration;
  }
  get missionProfile(): MissionProfile {
    return this._props.missionProfile;
  }
  get transportTrackingLink(): string | null {
    return this._props.transportTrackingLink;
  }
  get latestWeatherDeclineUnitId(): string | null {
    return this._props.latestWeatherDeclineUnitId;
  }
  get latestWeatherDeclineTime(): Date | null {
    return this._props.latestWeatherDeclineTime;
  }
  get latestWeatherDeclineReasonId(): string | null {
    return this._props.latestWeatherDeclineReasonId;
  }
  get latestWeatherDeclineSecondaryReasonId(): string | null {
    return this._props.latestWeatherDeclineSecondaryReasonId;
  }
  get webOrderCallType(): WebOrderCallType | null {
    return this._props.webOrderCallType;
  }
  get etloInMinutes(): number | null {
    return this._props.etloInMinutes;
  }
  get atloInMinutes(): number | null {
    return this._props.atloInMinutes;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAssigned() {
    return this._props.status === RequestStatus.Assigned;
  }
  public get isAssignmentDelayed() {
    return this._props.status === RequestStatus.AssignmentDelayed;
  }
  public get isCallReceived() {
    return this._props.status === RequestStatus.CallReceived;
  }
  public get isCancelled() {
    return this._props.status === RequestStatus.Cancelled;
  }
  public get isOffered() {
    return this._props.status === RequestStatus.Offered;
  }
  public get isOffering() {
    return this._props.status === RequestStatus.Offering;
  }
  public get isAir() {
    return this._props.requestedVehicleType === VehicleType.Air;
  }
  public get isFixedWing() {
    return this._props.requestedVehicleType === VehicleType.FixedWing;
  }
  public get isGround() {
    return this._props.requestedVehicleType === VehicleType.Ground;
  }
  public get isRotorWing() {
    return this._props.requestedVehicleType === VehicleType.RotorWing;
  }
  public get isRequestedVehicleTypeUnspecified() {
    return this._props.requestedVehicleType === VehicleType.Unspecified;
  }
  public get isMissionConfigurationUnspecified() {
    return this._props.missionConfiguration === MissionConfiguration.Unspecified;
  }
  public get isMissionProfileUnspecified() {
    return this._props.missionProfile === MissionProfile.Unspecified;
  }
  public get isGroundRun() {
    return this._props.requestedOperationType === OperationType.GroundRun;
  }
  public get isMaintenance() {
    return this._props.requestedOperationType === OperationType.Maintenance;
  }
  public get isNonPatientShuttle() {
    return this._props.requestedOperationType === OperationType.NonPatientShuttle;
  }
  public get isPatientShuttle() {
    return this._props.requestedOperationType === OperationType.PatientShuttle;
  }
  public get isPublicRelations() {
    return this._props.requestedOperationType === OperationType.PublicRelations;
  }
  public get isPublicUtility() {
    return this._props.requestedOperationType === OperationType.PublicUtility;
  }
  public get isReposition() {
    return this._props.requestedOperationType === OperationType.Reposition;
  }
  public get isResidence() {
    return this._props.requestedOperationType === OperationType.Residence;
  }
  public get isSceneCall() {
    return this._props.requestedOperationType === OperationType.SceneCall;
  }
  public get isSearchAndAssist() {
    return this._props.requestedOperationType === OperationType.SearchAndAssist;
  }
  public get isTraining() {
    return this._props.requestedOperationType === OperationType.Training;
  }
  public get isTransfer() {
    return this._props.requestedOperationType === OperationType.Transfer;
  }
  public get isTransferCenter() {
    return this._props.requestedOperationType === OperationType.TransferCenter;
  }
  public get isPhone() {
    return this._props.initiationType === RequestInitiationType.Phone;
  }
  public get isRapid() {
    return this._props.initiationType === RequestInitiationType.Rapid;
  }
  public get isStandard() {
    return this._props.initiationType === RequestInitiationType.Standard;
  }
  public get isInitiationTypeWebOrder() {
    return this._props.initiationType === RequestInitiationType.WebOrder;
  }
  public get isWebOrderCallTypeWebOrder() {
    return this._props.webOrderCallType === WebOrderCallType.WebOrder;
  }
  public get isShortestDistance() {
    return this._props.transportOfferAlgorithm === TransportOfferAlgorithm.ShortestDistance;
  }
  public get isShortestEta() {
    return this._props.transportOfferAlgorithm === TransportOfferAlgorithm.ShortestEta;
  }
  public get isIfr() {
    return this._props.minimumFlightRule === FlightRule.Ifr;
  }
  public get isNone() {
    return this._props.minimumFlightRule === FlightRule.None;
  }
  public get isVfr() {
    return this._props.minimumFlightRule === FlightRule.Vfr;
  }
  public get isCommunityRequest() {
    return this._props.missionConfiguration === MissionConfiguration.CommunityRequest;
  }
  public get isContractServices() {
    return this._props.missionConfiguration === MissionConfiguration.ContractServices;
  }
  public get isGovernmentIncident() {
    return this._props.missionConfiguration === MissionConfiguration.GovernmentIncident;
  }
  public get isAerialOperation() {
    return this._props.missionProfile === MissionProfile.AerialOperation;
  }
  public get isFireSuppression() {
    return this._props.missionProfile === MissionProfile.FireSuppression;
  }
  public get isHoistRescue() {
    return this._props.missionProfile === MissionProfile.HoistRescue;
  }
  public get isPhoneOrder() {
    return this._props.webOrderCallType === WebOrderCallType.PhoneOrder;
  }
  public get isRapidCall() {
    return this._props.webOrderCallType === WebOrderCallType.RapidCall;
  }
  public get isRapidSOS() {
    return this._props.webOrderCallType === WebOrderCallType.RapidSOS;
  }

  public isAnyStatus(...requestStatus: RequestStatus[]) {
    return requestStatus.includes(this._props.status);
  }
  public isAnyRequestedVehicleType(...vehicleType: VehicleType[]) {
    return (
      this._props.requestedVehicleType != null &&
      vehicleType.includes(this._props.requestedVehicleType)
    );
  }
  public isAnyRequestedOperationType(...operationType: OperationType[]) {
    return operationType.includes(this._props.requestedOperationType);
  }
  public isAnyInitiationType(...requestInitiationType: RequestInitiationType[]) {
    return (
      this._props.initiationType != null &&
      requestInitiationType.includes(this._props.initiationType)
    );
  }
  public isAnyTransportOfferAlgorithm(...transportOfferAlgorithm: TransportOfferAlgorithm[]) {
    return (
      this._props.transportOfferAlgorithm != null &&
      transportOfferAlgorithm.includes(this._props.transportOfferAlgorithm)
    );
  }
  public isAnyMinimumFlightRule(...flightRule: FlightRule[]) {
    return (
      this._props.minimumFlightRule != null && flightRule.includes(this._props.minimumFlightRule)
    );
  }
  public isAnyMissionConfiguration(...missionConfiguration: MissionConfiguration[]) {
    return missionConfiguration.includes(this._props.missionConfiguration);
  }
  public isAnyMissionProfile(...missionProfile: MissionProfile[]) {
    return missionProfile.includes(this._props.missionProfile);
  }
  public isAnyWebOrderCallType(...webOrderCallType: WebOrderCallType[]) {
    return (
      this._props.webOrderCallType != null &&
      webOrderCallType.includes(this._props.webOrderCallType)
    );
  }

  constructor(props: RequestProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        cancellationReasonId: this._props.cancellationReasonId,
        cancellationSecondaryReasonId: this._props.cancellationSecondaryReasonId,
        cancellationReasonNotes: this._props.cancellationReasonNotes,
        requestedVehicleType: this._props.requestedVehicleType,
        requestedOperationType: this._props.requestedOperationType,
        primaryUnitServiceAreaId: this._props.primaryUnitServiceAreaId,
        isStandbyRequest: this._props.isStandbyRequest,
        commitUnitToStandby: this._props.commitUnitToStandby,
        standbyActivationRequested: this._props.standbyActivationRequested,
        initiationType: this._props.initiationType,
        safetyQuestions: this._props.safetyQuestions,
        assignmentDelayedTime: this._props.assignmentDelayedTime,
        assignmentDelayedReasonId: this._props.assignmentDelayedReasonId,
        regionId: this._props.regionId,
        includeCompetitors: this._props.includeCompetitors,
        transportOfferAlgorithmId: this._props.transportOfferAlgorithmId,
        transportOfferAlgorithm: this._props.transportOfferAlgorithm,
        minimumFlightRule: this._props.minimumFlightRule,
        requiredCapabilities: this._props.requiredCapabilities,
        missionConfiguration: this._props.missionConfiguration,
        missionProfile: this._props.missionProfile,
        transportTrackingLink: this._props.transportTrackingLink,
        latestWeatherDeclineUnitId: this._props.latestWeatherDeclineUnitId,
        latestWeatherDeclineTime: this._props.latestWeatherDeclineTime,
        latestWeatherDeclineReasonId: this._props.latestWeatherDeclineReasonId,
        latestWeatherDeclineSecondaryReasonId: this._props.latestWeatherDeclineSecondaryReasonId,
        webOrderCallType: this._props.webOrderCallType,
        etloInMinutes: this._props.etloInMinutes,
        atloInMinutes: this._props.atloInMinutes,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRequestEntities = (state: AppState) => state.entityStores.request.entities;

export const selectRequestById = (id: string) => (state: AppState) =>
  state.entityStores.request.entities.get(id);

export const selectRequestMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.request.meta.get(id);

export const selectRequestAndMetadataById = (id: string) =>
  createSelector(selectRequestById(id), selectRequestMetadataById(id), (request, meta) => ({
    request,
    meta,
  }));

export function selectRequestEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRequestEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RequestRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRequestEntitiesByStatus(status: RequestStatus) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectRequestEntitiesByCancellationReasonId(cancellationReasonId: string) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.cancellationReasonId === cancellationReasonId).valueSeq(),
  );
}

export function selectRequestEntitiesByCancellationSecondaryReasonId(
  cancellationSecondaryReasonId: string,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities
      .filter((e) => e.cancellationSecondaryReasonId === cancellationSecondaryReasonId)
      .valueSeq(),
  );
}

export function selectRequestEntitiesByCancellationReasonNotes(cancellationReasonNotes: string) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.cancellationReasonNotes === cancellationReasonNotes).valueSeq(),
  );
}

export function selectRequestEntitiesByRequestedVehicleType(requestedVehicleType: VehicleType) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.requestedVehicleType === requestedVehicleType).valueSeq(),
  );
}

export function selectRequestEntitiesByRequestedOperationType(
  requestedOperationType: OperationType,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.requestedOperationType === requestedOperationType).valueSeq(),
  );
}

export function selectRequestEntitiesByPrimaryUnitServiceAreaId(primaryUnitServiceAreaId: string) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.primaryUnitServiceAreaId === primaryUnitServiceAreaId).valueSeq(),
  );
}

export function selectRequestEntitiesByIsStandbyRequest(isStandbyRequest: boolean) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.isStandbyRequest === isStandbyRequest).valueSeq(),
  );
}

export function selectRequestEntitiesByCommitUnitToStandby(commitUnitToStandby: boolean) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.commitUnitToStandby === commitUnitToStandby).valueSeq(),
  );
}

export function selectRequestEntitiesByStandbyActivationRequested(
  standbyActivationRequested: boolean,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.standbyActivationRequested === standbyActivationRequested).valueSeq(),
  );
}

export function selectRequestEntitiesByInitiationType(initiationType: RequestInitiationType) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.initiationType === initiationType).valueSeq(),
  );
}

export function selectRequestEntitiesByAssignmentDelayedTime(assignmentDelayedTime: Date) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.assignmentDelayedTime === assignmentDelayedTime).valueSeq(),
  );
}

export function selectRequestEntitiesByAssignmentDelayedReasonId(
  assignmentDelayedReasonId: string,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.assignmentDelayedReasonId === assignmentDelayedReasonId).valueSeq(),
  );
}

export function selectRequestEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectRequestEntitiesByIncludeCompetitors(includeCompetitors: boolean) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.includeCompetitors === includeCompetitors).valueSeq(),
  );
}

export function selectRequestEntitiesByTransportOfferAlgorithmId(
  transportOfferAlgorithmId: string,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.transportOfferAlgorithmId === transportOfferAlgorithmId).valueSeq(),
  );
}

export function selectRequestEntitiesByTransportOfferAlgorithm(
  transportOfferAlgorithm: TransportOfferAlgorithm,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.transportOfferAlgorithm === transportOfferAlgorithm).valueSeq(),
  );
}

export function selectRequestEntitiesByMinimumFlightRule(minimumFlightRule: FlightRule) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.minimumFlightRule === minimumFlightRule).valueSeq(),
  );
}

export function selectRequestEntitiesByMissionConfiguration(
  missionConfiguration: MissionConfiguration,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.missionConfiguration === missionConfiguration).valueSeq(),
  );
}

export function selectRequestEntitiesByMissionProfile(missionProfile: MissionProfile) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.missionProfile === missionProfile).valueSeq(),
  );
}

export function selectRequestEntitiesByTransportTrackingLink(transportTrackingLink: string) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.transportTrackingLink === transportTrackingLink).valueSeq(),
  );
}

export function selectRequestEntitiesByLatestWeatherDeclineUnitId(
  latestWeatherDeclineUnitId: string,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.latestWeatherDeclineUnitId === latestWeatherDeclineUnitId).valueSeq(),
  );
}

export function selectRequestEntitiesByLatestWeatherDeclineTime(latestWeatherDeclineTime: Date) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.latestWeatherDeclineTime === latestWeatherDeclineTime).valueSeq(),
  );
}

export function selectRequestEntitiesByLatestWeatherDeclineReasonId(
  latestWeatherDeclineReasonId: string,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities
      .filter((e) => e.latestWeatherDeclineReasonId === latestWeatherDeclineReasonId)
      .valueSeq(),
  );
}

export function selectRequestEntitiesByLatestWeatherDeclineSecondaryReasonId(
  latestWeatherDeclineSecondaryReasonId: string,
) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities
      .filter(
        (e) => e.latestWeatherDeclineSecondaryReasonId === latestWeatherDeclineSecondaryReasonId,
      )
      .valueSeq(),
  );
}

export function selectRequestEntitiesByWebOrderCallType(webOrderCallType: WebOrderCallType) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.webOrderCallType === webOrderCallType).valueSeq(),
  );
}

export function selectRequestEntitiesByEtloInMinutes(etloInMinutes: number) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.etloInMinutes === etloInMinutes).valueSeq(),
  );
}

export function selectRequestEntitiesByAtloInMinutes(atloInMinutes: number) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.atloInMinutes === atloInMinutes).valueSeq(),
  );
}

export function selectRequestEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllRequestEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
